import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/layout/AppChrome";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { useI18n } from "@/contexts/I18nContext";
import { ArrowUpRight } from "lucide-react";

/**
 * Shared layout for public legal pages (privacy, terms).
 * - Same PublicHeader + MarketingFooter as landing.
 * - Bilingual via useI18n().
 * - RTL flip handled by the I18nProvider.
 *
 * `sections` shape:
 *   [{ title: string, paragraphs: string[], list?: string[] }, ...]
 */
export default function LegalPage({ testId, eyebrow, title, updatedAt, intro, sections }) {
  const { lang } = useI18n();

  useEffect(() => {
    // scroll to top on route change
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div data-testid={testId} className="min-h-screen bg-background flex flex-col">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden pt-16 md:pt-24 pb-10 md:pb-14 border-b border-border/40">
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(1200px 400px at 20% 0%, rgba(184,135,93,0.10), transparent 60%), radial-gradient(1200px 400px at 80% 0%, rgba(0,47,167,0.10), transparent 60%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{eyebrow}</div>
          <h1 data-testid="legal-title" className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            {title}
          </h1>
          <div className="mt-4 text-sm text-muted-foreground">
            {lang === "ar" ? "آخر تحديث: " : "Last updated: "}
            <span className="text-foreground/80">{updatedAt}</span>
          </div>
          {intro && (
            <p className="mt-6 text-base md:text-lg text-foreground/85 leading-relaxed max-w-3xl">
              {intro}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
          <article className="space-y-10">
            {sections.map((s, i) => (
              <section key={i} data-testid={`legal-section-${i + 1}`} className="scroll-mt-24">
                <h2 className="font-display text-2xl md:text-3xl mb-4">
                  <span className="text-[#B8875D] text-sm align-top me-2">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </h2>
                {s.paragraphs?.map((p, k) => (
                  <p key={k} className="text-foreground/85 leading-relaxed mb-3">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-3 space-y-2 ps-6 list-disc marker:text-[#B8875D] text-foreground/85 leading-relaxed">
                    {s.list.map((li, k) => <li key={k}>{li}</li>)}
                  </ul>
                )}
              </section>
            ))}
          </article>

          {/* Cross-link between legal pages */}
          <div className="mt-16 pt-8 border-t border-border/50 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {lang === "ar" ? "لديك سؤال قانوني؟" : "Have a legal question?"}{" "}
              <a href="mailto:support@getdaam.com" className="text-primary hover:underline">support@getdaam.com</a>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Link to="/privacy" data-testid="legal-goto-privacy" className="inline-flex items-center gap-1.5 hover:text-primary">
                {lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy"} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <span className="text-border">•</span>
              <Link to="/terms" data-testid="legal-goto-terms" className="inline-flex items-center gap-1.5 hover:text-primary">
                {lang === "ar" ? "شروط الاستخدام" : "Terms of Service"} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
